import React, { useEffect, useState } from "react";
import { Amplify } from "aws-amplify";
import {
  Authenticator,
  CheckboxField,
  TextField,
  View,
  Flex,
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { Box } from "theme-ui";
import {
  Button as LumosButton,
  AlertInline,
  useNotify,
} from "@loanpal/lumos__core";
import NavBar from "../../shared/nav-bar";
import { formFields, components } from "../../utils/authentication";
import Loading from "../../utils/loading";
import {
  ADMIN_ERROR_EMAIL_TEMPLATES,
} from "../../utils/admin-error-email-templates";
import {
  getAdminErrorEmailConfig,
  saveAdminErrorEmailConfig,
} from "../../utils/api";
import awsExports from "../../aws-exports";
import "./admin-error-email-settings.css";

Amplify.configure(awsExports);

const CONFIGURABLE_TEMPLATES = ADMIN_ERROR_EMAIL_TEMPLATES.filter(
  (t) => t.id !== "T00-OTHER"
);

const AdminErrorEmailSettings = () => {
  const { addToast } = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [source, setSource] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [testRecipient, setTestRecipient] = useState("");
  const [enabledIds, setEnabledIds] = useState(() => new Set());
  const [templateRoutes, setTemplateRoutes] = useState({});

  const applyConfig = (config) => {
    setEnabledIds(new Set(config?.autoEmailTemplateIds || []));
    setTestRecipient(config?.testRecipient || "");
    setTemplateRoutes(config?.templateTestRecipients || {});
    setSource(config?.source || "");
    setUpdatedAt(config?.updatedAt || null);
  };

  const loadConfig = async () => {
    setLoading(true);
    setError("");
    try {
      const config = await getAdminErrorEmailConfig();
      applyConfig(config);
    } catch (err) {
      console.error(err);
      setError(
        "Could not load email alert settings. Deploy the API/function routes if this is the first time."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTemplate = (id, checked) => {
    setEnabledIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const setTemplateRoute = (id, email) => {
    setTemplateRoutes((prev) => ({
      ...prev,
      [id]: email,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const cleanedRoutes = {};
      for (const [id, email] of Object.entries(templateRoutes)) {
        const trimmed = String(email ?? "").trim();
        if (trimmed) cleanedRoutes[id] = trimmed;
      }
      const saved = await saveAdminErrorEmailConfig({
        autoEmailTemplateIds: [...enabledIds],
        testRecipient,
        templateTestRecipients: cleanedRoutes,
      });
      applyConfig(saved);
      addToast({
        variant: "success",
        message: "Email alert settings saved",
      });
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to save settings");
      addToast({
        variant: "error",
        message: "Failed to save email alert settings",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Authenticator formFields={formFields} components={components}>
      {({ signOut }) => (
        <>
          <NavBar>
            <LumosButton onClick={signOut} style={styles.signOut}>
              Sign out
            </LumosButton>
          </NavBar>
          <Box
            className="admin-error-email-settings"
            sx={{ p: 3, maxWidth: 920, mx: "auto" }}
          >
            <h1 className="admin-error-email-settings-title">
              Error email alerts
            </h1>
            <p className="admin-error-email-settings-subtitle">
              Enable templates to auto-send, and optionally set route-to email(s)
              for testing (To only, no CC). Use commas for multiple addresses.
              Per-template route wins over the global test recipient. Leave both
              blank for production partner / admin routing.
            </p>

            {loading ? (
              <Loading />
            ) : (
              <View>
                {error ? (
                  <Box sx={{ mb: 3 }}>
                    <AlertInline variant="error">{error}</AlertInline>
                  </Box>
                ) : null}

                <div className="admin-error-email-settings-meta">
                  Source: {source || "unknown"}
                  {updatedAt ? ` · Last saved ${updatedAt}` : ""}
                </div>

                <Flex gap="0.75rem" style={{ marginBottom: 16 }}>
                  <LumosButton
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save settings"}
                  </LumosButton>
                  <LumosButton
                    variant="secondary"
                    onClick={loadConfig}
                    disabled={saving || loading}
                  >
                    Reload
                  </LumosButton>
                </Flex>

                <Box sx={{ mb: 3 }}>
                  <h2 className="admin-error-email-settings-section-title">
                    Global test recipient (fallback)
                  </h2>
                  <TextField
                    label="Global test email (optional)"
                    labelHidden
                    size="small"
                    placeholder="you+errors@goodleap.com, teammate@goodleap.com"
                    value={testRecipient}
                    onChange={(e) => setTestRecipient(e.target.value)}
                    descriptiveText="Comma-separated. Used only when a template has no route-to email of its own."
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <h2 className="admin-error-email-settings-section-title">
                    Auto-send templates
                  </h2>
                  <Flex direction="column" gap="0.75rem">
                    {CONFIGURABLE_TEMPLATES.map((template) => (
                      <div
                        key={template.id}
                        className="admin-error-email-settings-row"
                      >
                        <CheckboxField
                          name={template.id}
                          size="small"
                          label={
                            <span>
                              <strong>{template.id}</strong>
                              {` — ${template.label || template.issueDetected}`}
                            </span>
                          }
                          checked={enabledIds.has(template.id)}
                          onChange={(e) =>
                            toggleTemplate(template.id, e.target.checked)
                          }
                        />
                        <div className="admin-error-email-settings-route">
                          <TextField
                            label={`Route ${template.id} to`}
                            labelHidden
                            size="small"
                            placeholder="a@goodleap.com, b@goodleap.com"
                            value={templateRoutes[template.id] || ""}
                            onChange={(e) =>
                              setTemplateRoute(template.id, e.target.value)
                            }
                            descriptiveText="Comma-separated test override for this template only."
                          />
                        </div>
                      </div>
                    ))}
                  </Flex>
                </Box>

                <Flex gap="0.75rem">
                  <LumosButton
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save settings"}
                  </LumosButton>
                  <LumosButton
                    variant="secondary"
                    onClick={loadConfig}
                    disabled={saving || loading}
                  >
                    Reload
                  </LumosButton>
                </Flex>
              </View>
            )}
          </Box>
        </>
      )}
    </Authenticator>
  );
};

const styles = {
  signOut: { marginRight: 10 },
};

export default AdminErrorEmailSettings;
